import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  IconButton,
  Chip,
  Paper,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Checkbox,
  Alert,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  QuestionAnswer as QuestionIcon,
  TextFields as TextIcon,
  CheckBox as CheckboxIcon,
  RadioButtonChecked as RadioIcon,
  ShortText as ShortTextIcon,
  Schedule as DateIcon,
  Description as TermsIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useSupabase } from '../contexts/SupabaseContext';

interface Question {
  id: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'yes-no' | 'multiple-choice' | 'checkbox' | 'terms-conditions';
  text: string; // Default language (Dutch)
  translations?: {
    [language: string]: {
      text?: string;
      options?: string[]; // For yes/no, multiple choice, checkbox options
    }
  };
  required: boolean;
  marketingQuestion?: boolean; // If true, response will be stored in form_responses table
  options?: string[]; // Default language options (Dutch)
  allowMultiple?: boolean; // For checkbox type: allow multiple selections
  termsContent?: string; // For terms-conditions questions (base64 encoded file content)
  termsFileName?: string; // For terms-conditions questions (original filename)
  termsAgreementText?: string; // For terms-conditions questions (agreement text in Dutch)
  termsAgreementTextEn?: string; // For terms-conditions questions (agreement text in English)
  termsLinkText?: string; // For terms-conditions questions (link text in Dutch)
  termsLinkTextEn?: string; // For terms-conditions questions (link text in English)
  order: number;
}

interface FormTemplate {
  id?: string;
  name: string;
  description: string;
  questions: Question[];
  isActive: boolean;
}

const FormBuilder: React.FC = () => {
  const { supabase } = useSupabase();
  const queryClient = useQueryClient();
  
  const [selectedTemplate, setSelectedTemplate] = useState<FormTemplate | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isQuestionDialogOpen, setIsQuestionDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<Question | null>(null);
  const [questionIndex, setQuestionIndex] = useState<number>(-1);
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [deleteQuestionDialogOpen, setDeleteQuestionDialogOpen] = useState(false);
  const [questionToDelete, setQuestionToDelete] = useState<{question: Question, index: number} | null>(null);

  // Fetch existing templates
  const { data: templates, isLoading } = useQuery('form-templates', async () => {
    try {
      const { data, error } = await supabase
        .from('form_templates')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      return (data as any[]) || [];
    } catch (err) {
      console.error('Error fetching templates:', err);
      return [];
    }
  });

  // Save template mutation
  const saveTemplate = useMutation(async (template: FormTemplate) => {
    setIsSaving(true);
    setError(null);
    
    try {
      const config = {
        questions: template.questions,
        sections: [
          {
            id: 'questions',
            title: 'Questions',
            type: 'questions',
            questions: template.questions.map(q => q.id)
          }
        ]
      };

      if (template.id) {
        // Update existing template
        const { error } = await (supabase as any)
          .from('form_templates')
          .update({
            name: template.name,
            description: template.description,
            config: config,
            is_active: template.isActive,
          })
          .eq('id', template.id);
        
        if (error) {
          console.error('Update error:', error);
          throw error;
        }
      } else {
        // Create new template
        const { error } = await (supabase as any)
          .from('form_templates')
          .insert({
            name: template.name,
            description: template.description,
            config: config,
            is_active: template.isActive,
            created_by: 'admin',
          });
        
        if (error) {
          console.error('Insert error:', error);
          throw error;
        }
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Failed to save template. Please try again.');
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries('form-templates');
      setIsEditDialogOpen(false);
      setSelectedTemplate(null);
      setError(null);
      setSuccessMessage('Template saved successfully!');
      setTimeout(() => setSuccessMessage(null), 3000);
    },
    onError: () => {
      setError('Failed to save template. Please try again.');
    }
  });

  const handleCreateTemplate = () => {
    setSelectedTemplate({
      name: '',
      description: '',
      questions: [],
      isActive: false
    });
    setIsEditDialogOpen(true);
    setError(null);
  };

  const handleEditTemplate = (template: any) => {
    const questions = template.config?.questions || [];
    setSelectedTemplate({
      id: template.id,
      name: template.name,
      description: template.description || '',
      questions: questions,
      isActive: template.is_active
    });
    setIsEditDialogOpen(true);
    setError(null);
  };

  const handleAddQuestion = () => {
    setEditingQuestion({
      id: `q_${Date.now()}`,
      type: 'text',
      text: '',
      required: false,
      marketingQuestion: false,
      order: selectedTemplate?.questions.length || 0,
      translations: {
        en: {
          text: '',
          options: ['Yes', 'No'] // Default yes/no options
        }
      },
      options: ['Ja', 'Nee'] // Default Dutch yes/no options
    });
    setQuestionIndex(-1);
    setIsQuestionDialogOpen(true);
  };

  const handleEditQuestion = (question: Question, index: number) => {
    setEditingQuestion({ ...question });
    setQuestionIndex(index);
    setIsQuestionDialogOpen(true);
  };

  const handleDeleteQuestion = (question: Question, index: number) => {
    setQuestionToDelete({ question, index });
    setDeleteQuestionDialogOpen(true);
  };

  const confirmDeleteQuestion = () => {
    if (!questionToDelete || !selectedTemplate) return;
    
    const newQuestions = selectedTemplate.questions.filter((_, i) => i !== questionToDelete.index);
    setSelectedTemplate({
      ...selectedTemplate,
      questions: newQuestions.map((q, i) => ({ ...q, order: i }))
    });
    
    setDeleteQuestionDialogOpen(false);
    setQuestionToDelete(null);
  };

  const handleSaveQuestion = () => {
    if (editingQuestion && selectedTemplate) {
      let newQuestions = [...selectedTemplate.questions];
      
      if (questionIndex >= 0) {
        // Edit existing question
        newQuestions[questionIndex] = editingQuestion;
      } else {
        // Add new question
        newQuestions.push({ ...editingQuestion, order: newQuestions.length });
      }
      
      setSelectedTemplate({
        ...selectedTemplate,
        questions: newQuestions
      });
      
      setIsQuestionDialogOpen(false);
      setEditingQuestion(null);
    }
  };

  const getQuestionIcon = (type: string) => {
    switch (type) {
      case 'text': return <ShortTextIcon />;
      case 'textarea': return <TextIcon />;
      case 'number': return <TextIcon />;
      case 'date': return <DateIcon />;
      case 'yes-no': return <RadioIcon />;
      case 'multiple-choice': return <RadioIcon />;
      case 'checkbox': return <CheckboxIcon />;
      case 'terms-conditions': return <TermsIcon />;
      default: return <QuestionIcon />;
    }
  };

  const getQuestionTypeLabel = (type: string) => {
    switch (type) {
      case 'text': return 'Short Text';
      case 'textarea': return 'Long Text';
      case 'number': return 'Number';
      case 'date': return 'Date';
      case 'yes-no': return 'Yes/No';
      case 'multiple-choice': return 'Multiple Choice';
      case 'checkbox': return 'Checkbox';
      case 'terms-conditions': return 'Terms & Conditions';
      default: return type;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Form Builder
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateTemplate}
        >
          Create New Template
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Template List */}
      <Grid container spacing={3}>
        {templates?.map((template: any) => (
          <Grid item xs={12} md={6} lg={4} key={template.id}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                  <Typography variant="h6" component="div">
                    {template.name}
                  </Typography>
                  <Chip
                    label={template.is_active ? 'Active' : 'Draft'}
                    color={template.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {template.description || 'No description'}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<EditIcon />}
                  onClick={() => handleEditTemplate(template)}
                  fullWidth
                >
                  Edit
                </Button>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Edit Template Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={() => setIsEditDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedTemplate?.id ? 'Edit Template' : 'Create New Template'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Template Name"
                value={selectedTemplate?.name || ''}
                onChange={(e) => setSelectedTemplate(prev => prev ? { ...prev, name: e.target.value } : null)}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={2}
                value={selectedTemplate?.description || ''}
                onChange={(e) => setSelectedTemplate(prev => prev ? { ...prev, description: e.target.value } : null)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={selectedTemplate?.isActive || false}
                    onChange={(e) => setSelectedTemplate(prev => prev ? { ...prev, isActive: e.target.checked } : null)}
                  />
                }
                label="Active Template"
              />
            </Grid>
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Questions</Typography>
                <Button
                  startIcon={<AddIcon />}
                  onClick={handleAddQuestion}
                  disabled={!selectedTemplate?.name}
                >
                  Add Question
                </Button>
              </Box>
              <Paper variant="outlined" sx={{ p: 2, minHeight: 200 }}>
                {selectedTemplate?.questions.length === 0 ? (
                  <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                    No questions added yet. Click "Add Question" to get started.
                  </Typography>
                ) : (
                  <List>
                    {selectedTemplate?.questions.map((question, index) => (
                      <ListItem
                        key={question.id}
                        sx={{ border: '1px solid #e0e0e0', borderRadius: 1, mb: 1 }}
                      >
                        <ListItemIcon>
                          {getQuestionIcon(question.type)}
                        </ListItemIcon>
                        <ListItemText
                          primary={question.text}
                          secondary={`${getQuestionTypeLabel(question.type)} ${question.required ? '(Required)' : '(Optional)'}`}
                        />
                        <IconButton
                          size="small"
                          onClick={() => handleEditQuestion(question, index)}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => handleDeleteQuestion(question, index)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Paper>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsEditDialogOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => saveTemplate.mutate(selectedTemplate!)}
            variant="contained"
            startIcon={isSaving ? null : <SaveIcon />}
            disabled={!selectedTemplate?.name || selectedTemplate.questions.length === 0 || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Template'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Question Dialog */}
      <Dialog
        open={isQuestionDialogOpen}
        onClose={() => setIsQuestionDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {questionIndex >= 0 ? 'Edit Question' : 'Add Question'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Question Text (Dutch - Default)"
                value={editingQuestion?.text || ''}
                onChange={(e) => setEditingQuestion(prev => prev ? { ...prev, text: e.target.value } : null)}
              />
            </Grid>
            
            {/* English Translation */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Question Text (English)"
                value={editingQuestion?.translations?.en?.text || ''}
                onChange={(e) => setEditingQuestion(prev => prev ? { 
                  ...prev, 
                  translations: {
                    ...prev.translations,
                    en: {
                      ...prev.translations?.en,
                      text: e.target.value
                    }
                  }
                } : null)}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel sx={{ left: 14 }}>Question Type</InputLabel>
                <Select
                  value={editingQuestion?.type || 'text'}
                  onChange={(e) => setEditingQuestion(prev => prev ? { ...prev, type: e.target.value as any } : null)}
                >
                  <MenuItem value="text">Short Text</MenuItem>
                  <MenuItem value="textarea">Long Text</MenuItem>
                  <MenuItem value="number">Number</MenuItem>
                  <MenuItem value="date">Date</MenuItem>
                  <MenuItem value="yes-no">Yes/No</MenuItem>
                  <MenuItem value="multiple-choice">Multiple Choice</MenuItem>
                  <MenuItem value="checkbox">Checkbox</MenuItem>
                  <MenuItem value="terms-conditions">Terms & Conditions</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            {/* Allow Multiple Selections for Checkbox */}
            {editingQuestion?.type === 'checkbox' && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={editingQuestion?.allowMultiple || false}
                      onChange={(e) => setEditingQuestion(prev => prev ? { 
                        ...prev, 
                        allowMultiple: e.target.checked 
                      } : null)}
                    />
                  }
                  label="Allow multiple selections"
                />
              </Grid>
            )}
            
            {(editingQuestion?.type === 'multiple-choice' || editingQuestion?.type === 'checkbox') && (
              <>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Options (Dutch - Default, one per line)"
                    multiline
                    rows={5}
                    maxRows={15}
                    variant="outlined"
                    inputProps={{
                      style: { 
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        resize: 'vertical'
                      },
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          const target = e.target as HTMLTextAreaElement;
                          const start = target.selectionStart;
                          const end = target.selectionEnd;
                          const value = target.value;
                          const newValue = value.substring(0, start) + '\n' + value.substring(end);
                          target.value = newValue;
                          target.selectionStart = target.selectionEnd = start + 1;
                          target.dispatchEvent(new Event('input', { bubbles: true }));
                        }
                      }
                    }}
                    placeholder="Optie 1&#10;Optie 2&#10;Optie 3&#10;Optie 4&#10;Optie 5&#10;Optie 6&#10;Optie 7&#10;Optie 8&#10;Optie 9&#10;Optie 10"
                    value={editingQuestion?.options?.join('\n') || ''}
                    onChange={(e) => setEditingQuestion(prev => prev ? { 
                      ...prev, 
                      options: e.target.value.split('\n').filter(opt => opt.trim()) 
                    } : null)}
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Options (English, one per line)"
                    multiline
                    rows={5}
                    maxRows={15}
                    variant="outlined"
                    inputProps={{
                      style: { 
                        whiteSpace: 'pre-wrap',
                        wordWrap: 'break-word',
                        resize: 'vertical'
                      },
                      onKeyDown: (e) => {
                        if (e.key === 'Enter' && !e.shiftKey) {
                          e.preventDefault();
                          const target = e.target as HTMLTextAreaElement;
                          const start = target.selectionStart;
                          const end = target.selectionEnd;
                          const value = target.value;
                          const newValue = value.substring(0, start) + '\n' + value.substring(end);
                          target.value = newValue;
                          target.selectionStart = target.selectionEnd = start + 1;
                          target.dispatchEvent(new Event('input', { bubbles: true }));
                        }
                      }
                    }}
                    placeholder="Option 1&#10;Option 2&#10;Option 3&#10;Option 4&#10;Option 5&#10;Option 6&#10;Option 7&#10;Option 8&#10;Option 9&#10;Option 10"
                    value={editingQuestion?.translations?.en?.options?.join('\n') || ''}
                    onChange={(e) => setEditingQuestion(prev => prev ? { 
                      ...prev, 
                      translations: {
                        ...prev.translations,
                        en: {
                          ...prev.translations?.en,
                          options: e.target.value.split('\n').filter(opt => opt.trim())
                        }
                      }
                    } : null)}
                  />
                </Grid>
              </>
            )}
            
            {/* Yes/No Translation */}
            {editingQuestion?.type === 'yes-no' && (
              <>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Yes Text (Dutch)"
                    value={editingQuestion?.options?.[0] || 'Ja'}
                    onChange={(e) => setEditingQuestion(prev => prev ? { 
                      ...prev, 
                      options: [e.target.value, prev.options?.[1] || 'Nee']
                    } : null)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="No Text (Dutch)"
                    value={editingQuestion?.options?.[1] || 'Nee'}
                    onChange={(e) => setEditingQuestion(prev => prev ? { 
                      ...prev, 
                      options: [prev.options?.[0] || 'Ja', e.target.value]
                    } : null)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="Yes Text (English)"
                    value={editingQuestion?.translations?.en?.options?.[0] || 'Yes'}
                    onChange={(e) => setEditingQuestion(prev => prev ? { 
                      ...prev, 
                      translations: {
                        ...prev.translations,
                        en: {
                          ...prev.translations?.en,
                          options: [e.target.value, prev.translations?.en?.options?.[1] || 'No']
                        }
                      }
                    } : null)}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="No Text (English)"
                    value={editingQuestion?.translations?.en?.options?.[1] || 'No'}
                    onChange={(e) => setEditingQuestion(prev => prev ? { 
                      ...prev, 
                      translations: {
                        ...prev.translations,
                        en: {
                          ...prev.translations?.en,
                          options: [prev.translations?.en?.options?.[0] || 'Yes', e.target.value]
                        }
                      }
                    } : null)}
                  />
                </Grid>
              </>
            )}
            
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editingQuestion?.required || false}
                    onChange={(e) => setEditingQuestion(prev => prev ? { ...prev, required: e.target.checked } : null)}
                  />
                }
                label="Required Question"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={editingQuestion?.marketingQuestion || false}
                    onChange={(e) => setEditingQuestion(prev => prev ? { ...prev, marketingQuestion: e.target.checked } : null)}
                  />
                }
                label="Marketing Question (store response in database)"
              />
            </Grid>
            
            {/* Terms & Conditions File Upload */}
            {editingQuestion?.type === 'terms-conditions' && (
              <>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Upload Terms & Conditions Document
                  </Typography>
                  <input
                    type="file"
                    accept=".pdf,.txt"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) {
                        const reader = new FileReader();
                        reader.onload = (event) => {
                          const content = event.target?.result as string;
                          // Save terms content to the question being edited
                          setEditingQuestion(prev => prev ? {
                            ...prev,
                            termsContent: content,
                            termsFileName: file.name
                          } : null);
                        };
                        reader.readAsDataURL(file);
                      }
                    }}
                    style={{ marginBottom: 16, width: '100%' }}
                  />
                  {editingQuestion?.termsFileName && (
                    <Typography variant="body2" color="text.secondary">
                      Uploaded: {editingQuestion.termsFileName}
                    </Typography>
                  )}
                </Grid>
                
                {/* Agreement Text - Dutch (Default) */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Agreement Text (Dutch)"
                    value={editingQuestion?.termsAgreementText || ''}
                    onChange={(e) => setEditingQuestion(prev => prev ? {
                      ...prev,
                      termsAgreementText: e.target.value
                    } : null)}
                    placeholder="I agree to the terms and conditions"
                    multiline
                    rows={2}
                    helperText="Text shown next to the checkbox for agreement"
                  />
                </Grid>
                
                {/* Agreement Text - English */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Agreement Text (English)"
                    value={editingQuestion?.termsAgreementTextEn || ''}
                    onChange={(e) => setEditingQuestion(prev => prev ? {
                      ...prev,
                      termsAgreementTextEn: e.target.value
                    } : null)}
                    placeholder="I agree to the terms and conditions"
                    multiline
                    rows={2}
                    helperText="English translation of the agreement text"
                  />
                </Grid>
                
                {/* Link Text - Dutch (Default) */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Link Text (Dutch)"
                    value={editingQuestion?.termsLinkText || ''}
                    onChange={(e) => setEditingQuestion(prev => prev ? {
                      ...prev,
                      termsLinkText: e.target.value
                    } : null)}
                    placeholder="View Terms & Conditions"
                    helperText="Text for the clickable link to view the document"
                  />
                </Grid>
                
                {/* Link Text - English */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Link Text (English)"
                    value={editingQuestion?.termsLinkTextEn || ''}
                    onChange={(e) => setEditingQuestion(prev => prev ? {
                      ...prev,
                      termsLinkTextEn: e.target.value
                    } : null)}
                    placeholder="View Terms & Conditions"
                    helperText="English translation of the link text"
                  />
                </Grid>
              </>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIsQuestionDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveQuestion}
            variant="contained"
            disabled={!editingQuestion?.text}
          >
            Save Question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Question Confirmation Dialog */}
      <Dialog open={deleteQuestionDialogOpen} onClose={() => setDeleteQuestionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Question Deletion</DialogTitle>
        <DialogContent>
          <Box textAlign="center" p={2}>
            <Typography variant="h6" gutterBottom>
              Are you sure you want to delete this question?
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Question: <strong>{questionToDelete?.question.text}</strong>
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Type: {getQuestionTypeLabel(questionToDelete?.question.type || 'text')}
            </Typography>
            <Alert severity="warning" sx={{ mt: 2 }}>
              This action cannot be undone. You will need to recreate the question if needed.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteQuestionDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={confirmDeleteQuestion} 
            variant="contained" 
            color="error"
          >
            Delete Question
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FormBuilder;
